import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@root/components/Button';
import Input from '@root/components/Input';
import SafeAreaInset from '@root/components/SafeAreaInset';
import { CellCard, CellCommon, CellTitle } from '@root/components/Cell';
import useBillAddress from '@root/hooks/bill/useBillAddress';
import { countryData } from '@root/constant';
import './style.less';
import { isAndroid, isInApp } from '@root/shared';

const BillAddress = () => {
  const containerRef = useRef<any>();
  const placeholderRef = useRef<any>();
  const { t } = useTranslation();
  const {
    state: { postalErrMsg, finalData, saveDisabled, requesting },
    action: {
      getAddress,
      onSelectCountry,
      onChangeZipCode,
      onSave,
      onChangeAddress,
    },
  } = useBillAddress();

  const { postal, county, city, state, address, countryCode } = finalData;

  const onFocus = useCallback((e: any) => {
    if (
      !(isInApp || isAndroid) ||
      !containerRef.current ||
      document.body.clientHeight / e.target.offsetTop > 2
    ) {
      return;
    }
    // HACK 安卓键盘会挡住 Input
    containerRef.current.style.maxHeight = '400px';
    placeholderRef.current.style.height = '200px';
    containerRef.current.scrollTop = 100;
  }, []);

  const onBlur = useCallback(() => {
    if (!(isInApp || isAndroid)) {
      return;
    }
    containerRef.current.style.maxHeight = 'none';
    placeholderRef.current.style.height = 'auto';
  }, []);

  const _countryName = useMemo(() => {
    const { countryName = '' } =
      countryData.find((i) => i.countryCode === countryCode) || {};
    return countryName;
  }, [countryCode]);

  const _usaAddress = useMemo(() => {
    if (!postal) {
      return '-';
    }
    if (!county) {
      return '-';
    }

    return `${county}, ${city}, ${state} ${postal}`;
  }, [city, county, postal, state]);

  return (
    <>
      <div className="flex-section-auto bill-address" ref={containerRef}>
        <CellTitle enhance paddingTop={20}>
          {t('bill_address_information')}
        </CellTitle>
        <CellTitle paddingTop={8}>
          <span className="bill-required">*</span>
          {t('bill_region')}
        </CellTitle>
        <CellCommon title={_countryName} arrow onClick={onSelectCountry} />

        <CellTitle>
          <span className="bill-required">*</span>
          {t('bill_portal')}
        </CellTitle>
        <CellCard marginTop={0} marginBottom={0}>
          <Input
            className="fill large"
            value={postal || ''}
            onChange={onChangeZipCode}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={t('bill_portal')}
            maxLength={10}
            edit
            clear
          />
        </CellCard>
        {!!postalErrMsg && <p className="bill-error-msg">{postalErrMsg}</p>}
        <CellTitle>
          <span className="bill-required">*</span>
          {t('bill_address')}
          {finalData.countryCode === 'USA' && !postalErrMsg && !!postal && (
            <span className="load-address" onClick={getAddress}>
              {t('get_address')}
            </span>
          )}
        </CellTitle>
        {finalData.countryCode === 'USA' ? (
          <CellCommon title={_usaAddress} />
        ) : (
          <CellCard marginTop={0}>
            <Input
              className="fill large"
              value={address || ''}
              onChange={onChangeAddress}
              placeholder={t('bill_address')}
              onFocus={onFocus}
              onBlur={onBlur}
              maxLength={200}
              edit
              clear
            />
          </CellCard>
        )}
        <div ref={placeholderRef} />
      </div>
      <div className="flex-section-footer">
        <Button disabled={saveDisabled} loading={requesting} onClick={onSave}>
          {t('ok')}
        </Button>
        <SafeAreaInset />
      </div>
    </>
  );
};

export default BillAddress;
